import React, { useState, useEffect, useCallback } from 'react';
import {
  systemSettingsService,
  PlatformConfig,
  ServiceCategory,
  NotificationTemplate,
  EmailTemplate,
  AuditLog,
  SystemHealth,
} from '../services/systemSettingsService';
import { Table, TableColumn } from '../components/Table';
import { PayoutCommissionPolicyPanel } from '../components/PayoutCommissionPolicyPanel';
import { parseServiceCategoriesCsv } from '../utils/parseServiceCategoriesCsv';
import './SystemSettingsPage.css';

type Tab = 'platform-config' | 'service-categories' | 'notification-templates' | 'email-templates' | 'system-health' | 'audit-logs' | 'data-tools';

export const SystemSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('platform-config');

  return (
    <div className="system-settings-page">
      <div className="system-settings-header">
        <h1>System Settings & Configuration</h1>
      </div>

      <div className="system-settings-tabs">
        <button
          className={`tab-button ${activeTab === 'platform-config' ? 'active' : ''}`}
          onClick={() => setActiveTab('platform-config')}
        >
          Platform Configuration
        </button>
        <button
          className={`tab-button ${activeTab === 'service-categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('service-categories')}
        >
          Service Categories
        </button>
        <button
          className={`tab-button ${activeTab === 'notification-templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('notification-templates')}
        >
          Notification Templates
        </button>
        <button
          className={`tab-button ${activeTab === 'email-templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('email-templates')}
        >
          Email Templates
        </button>
        <button
          className={`tab-button ${activeTab === 'system-health' ? 'active' : ''}`}
          onClick={() => setActiveTab('system-health')}
        >
          System Health
        </button>
        <button
          className={`tab-button ${activeTab === 'audit-logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit-logs')}
        >
          Audit Logs
        </button>
        <button
          className={`tab-button ${activeTab === 'data-tools' ? 'active' : ''}`}
          onClick={() => setActiveTab('data-tools')}
        >
          Data Tools
        </button>
      </div>

      <div className="system-settings-content">
        {activeTab === 'platform-config' && <PlatformConfigTab />}
        {activeTab === 'service-categories' && <ServiceCategoriesTab />}
        {activeTab === 'notification-templates' && <NotificationTemplatesTab />}
        {activeTab === 'email-templates' && <EmailTemplatesTab />}
        {activeTab === 'system-health' && <SystemHealthTab />}
        {activeTab === 'audit-logs' && <AuditLogsTab />}
        {activeTab === 'data-tools' && <DataToolsTab />}
      </div>
    </div>
  );
};

const PlatformConfigTab: React.FC = () => {
  const [configs, setConfigs] = useState<PlatformConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PlatformConfig | null>(null);

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadConfigs reads categoryFilter
  }, [categoryFilter]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const category = categoryFilter !== 'all' ? categoryFilter : undefined;
      const data = await systemSettingsService.listPlatformConfigs(category);
      setConfigs(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load platform configs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configKey: string) => {
    if (!window.confirm(`Are you sure you want to delete config "${configKey}"?`)) {
      return;
    }
    try {
      await systemSettingsService.deletePlatformConfig(configKey);
      await loadConfigs();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete config');
    }
  };

  const formatValue = (value: string | number | boolean | Record<string, unknown>) => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const columns: TableColumn<PlatformConfig>[] = [
    {
      header: 'Config Key',
      accessor: 'configKey',
      render: (value) => <div className="config-key">{value as string}</div>,
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (value) => <span className="category-badge">{value as string}</span>,
    },
    {
      header: 'Value',
      accessor: 'configValue',
      render: (value) => (
        <div className="config-value" title={formatValue(value as string | number | boolean | Record<string, unknown>)}>
          {formatValue(value as string | number | boolean | Record<string, unknown>).substring(0, 50)}
          {formatValue(value as string | number | boolean | Record<string, unknown>).length > 50 ? '...' : ''}
        </div>
      ),
    },
    {
      header: 'Updated',
      accessor: 'updatedAt',
      render: (value) => new Date(value as string).toLocaleDateString(),
      hideOnTablet: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button className="edit-button" onClick={() => setEditingConfig(row)}>Edit</button>
          <button className="delete-button" onClick={() => handleDelete(row.configKey)}>Delete</button>
        </div>
      ),
      dataLabel: '',
    },
  ];

  return (
    <div className="tab-content">
      <PayoutCommissionPolicyPanel />
      {error && <div className="error-message">{error}</div>}
      <div className="tab-header">
        <h2>Platform Configuration</h2>
        <div className="tab-actions">
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="filter-select">
            <option value="all">All Categories</option>
            <option value="pricing">Pricing</option>
            <option value="commission">Commission</option>
            <option value="booking">Booking</option>
            <option value="general">General</option>
            <option value="security">Security</option>
            <option value="notification">Notification</option>
          </select>
          <button onClick={() => setShowCreateModal(true)} className="create-button">Create Config</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading platform configs...</div>
      ) : (
        <Table
          columns={columns}
          data={configs}
          getRowId={(row) => row.configKey}
          emptyMessage="No platform configs found"
        />
      )}

      {showCreateModal && (
        <ConfigModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadConfigs();
          }}
        />
      )}

      {editingConfig && (
        <ConfigModal
          config={editingConfig}
          onClose={async () => {
            setEditingConfig(null);
            await loadConfigs();
          }}
        />
      )}
    </div>
  );
};

const ServiceCategoriesTab: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [importStatus, setImportStatus] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemSettingsService.listServiceCategories();
      setCategories(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load service categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    try {
      await systemSettingsService.deleteServiceCategory(categoryId);
      await loadCategories();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const handleCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportStatus(null);
    try {
      const text = await file.text();
      const { items, errors } = parseServiceCategoriesCsv(text);
      if (errors.length > 0) {
        setImportStatus({
          type: 'err',
          text: errors.map((er) => `Line ${er.line}: ${er.message}`).join('\n'),
        });
        return;
      }
      setImportBusy(true);
      const { imported } = await systemSettingsService.importServiceCategories(items);
      setImportStatus({ type: 'ok', text: `Imported ${imported} categories (upsert by categoryId).` });
      await loadCategories();
    } catch (err: unknown) {
      setImportStatus({
        type: 'err',
        text: err instanceof Error ? err.message : 'Import failed',
      });
    } finally {
      setImportBusy(false);
    }
  };

  const columns: TableColumn<ServiceCategory>[] = [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'Description',
      accessor: 'description',
      render: (value) => (value as string) || '-',
      hideOnTablet: true,
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Display Order',
      accessor: 'displayOrder',
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button className="edit-button" onClick={() => setEditingCategory(row)}>Edit</button>
          <button className="delete-button" onClick={() => handleDelete(row.categoryId)}>Delete</button>
        </div>
      ),
      dataLabel: '',
    },
  ];

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}
      <div className="tab-header">
        <h2>Service Categories</h2>
        <div className="tab-actions">
          <a
            className="download-button"
            href={`${import.meta.env.BASE_URL}service-categories-import-template.csv`}
            download="service-categories-import-template.csv"
          >
            Download CSV template
          </a>
          <label className={`create-button file-upload-label${importBusy ? ' disabled' : ''}`}>
            {importBusy ? 'Importing…' : 'Import CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              disabled={importBusy}
              onChange={handleCsvFile}
            />
          </label>
          <button type="button" onClick={() => setShowCreateModal(true)} className="create-button">
            Create Category
          </button>
        </div>
      </div>

      {importStatus && (
        <div className={`import-status ${importStatus.type}`}>
          <pre className="import-status-pre">{importStatus.text}</pre>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading service categories...</div>
      ) : (
        <Table
          columns={columns}
          data={categories}
          getRowId={(row) => row.categoryId}
          emptyMessage="No service categories found"
        />
      )}

      {showCreateModal && (
        <CategoryModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadCategories();
          }}
        />
      )}

      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          onClose={async () => {
            setEditingCategory(null);
            await loadCategories();
          }}
        />
      )}
    </div>
  );
};

const NotificationTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemSettingsService.listNotificationTemplates();
      setTemplates(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load notification templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      await systemSettingsService.deleteNotificationTemplate(templateId);
      await loadTemplates();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const columns: TableColumn<NotificationTemplate>[] = [
    {
      header: 'Template Type',
      accessor: 'templateType',
    },
    {
      header: 'Title',
      accessor: 'title',
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Updated',
      accessor: 'updatedAt',
      render: (value) => new Date(value as string).toLocaleDateString(),
      hideOnTablet: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button className="edit-button" onClick={() => setEditingTemplate(row)}>Edit</button>
          <button className="delete-button" onClick={() => handleDelete(row.templateId)}>Delete</button>
        </div>
      ),
      dataLabel: '',
    },
  ];

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}
      <div className="tab-header">
        <h2>Notification Templates</h2>
        <button onClick={() => setShowCreateModal(true)} className="create-button">Create Template</button>
      </div>

      {loading ? (
        <div className="loading">Loading notification templates...</div>
      ) : (
        <Table
          columns={columns}
          data={templates}
          getRowId={(row) => row.templateId}
          emptyMessage="No notification templates found"
        />
      )}

      {showCreateModal && (
        <NotificationTemplateModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadTemplates();
          }}
        />
      )}

      {editingTemplate && (
        <NotificationTemplateModal
          template={editingTemplate}
          onClose={async () => {
            setEditingTemplate(null);
            await loadTemplates();
          }}
        />
      )}
    </div>
  );
};

const EmailTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemSettingsService.listEmailTemplates();
      setTemplates(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      await systemSettingsService.deleteEmailTemplate(templateId);
      await loadTemplates();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const columns: TableColumn<EmailTemplate>[] = [
    {
      header: 'Template Type',
      accessor: 'templateType',
    },
    {
      header: 'Subject',
      accessor: 'subject',
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Updated',
      accessor: 'updatedAt',
      render: (value) => new Date(value as string).toLocaleDateString(),
      hideOnTablet: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button className="edit-button" onClick={() => setEditingTemplate(row)}>Edit</button>
          <button className="delete-button" onClick={() => handleDelete(row.templateId)}>Delete</button>
        </div>
      ),
      dataLabel: '',
    },
  ];

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}
      <div className="tab-header">
        <h2>Email Templates</h2>
        <button onClick={() => setShowCreateModal(true)} className="create-button">Create Template</button>
      </div>

      {loading ? (
        <div className="loading">Loading email templates...</div>
      ) : (
        <Table
          columns={columns}
          data={templates}
          getRowId={(row) => row.templateId}
          emptyMessage="No email templates found"
        />
      )}

      {showCreateModal && (
        <EmailTemplateModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadTemplates();
          }}
        />
      )}

      {editingTemplate && (
        <EmailTemplateModal
          template={editingTemplate}
          onClose={async () => {
            setEditingTemplate(null);
            await loadTemplates();
          }}
        />
      )}
    </div>
  );
};

const SystemHealthTab: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemSettingsService.getSystemHealth();
      setHealth(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading system health...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!health) {
    return <div className="empty-state">No health data available</div>;
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>System Health</h2>
        <button onClick={loadHealth} className="refresh-button">Refresh</button>
      </div>

      <div className="health-status">
        <div className={`health-indicator ${health.status}`}>
          <span className="health-status-text">{health.status.toUpperCase()}</span>
        </div>
        <div className="health-timestamp">Last updated: {new Date(health.timestamp).toLocaleString()}</div>
      </div>

      <div className="health-metrics">
        <div className="metric-card">
          <h3>Database</h3>
          <div className={`metric-status ${health.metrics.database.status}`}>
            {health.metrics.database.status}
          </div>
          {health.metrics.database.responseTime && (
            <div className="metric-value">Response Time: {health.metrics.database.responseTime}ms</div>
          )}
        </div>

        <div className="metric-card">
          <h3>API</h3>
          <div className={`metric-status ${health.metrics.api.status}`}>
            {health.metrics.api.status}
          </div>
          {health.metrics.api.averageResponseTime && (
            <div className="metric-value">Avg Response: {health.metrics.api.averageResponseTime}ms</div>
          )}
          {health.metrics.api.errorRate !== undefined && (
            <div className="metric-value">Error Rate: {(health.metrics.api.errorRate * 100).toFixed(2)}%</div>
          )}
        </div>

        <div className="metric-card">
          <h3>Lambda</h3>
          <div className={`metric-status ${health.metrics.lambda.status}`}>
            {health.metrics.lambda.status}
          </div>
          {health.metrics.lambda.averageDuration && (
            <div className="metric-value">Avg Duration: {health.metrics.lambda.averageDuration}ms</div>
          )}
          {health.metrics.lambda.errorRate !== undefined && (
            <div className="metric-value">Error Rate: {(health.metrics.lambda.errorRate * 100).toFixed(2)}%</div>
          )}
        </div>
      </div>

      {health.alerts && health.alerts.length > 0 && (
        <div className="health-alerts">
          <h3>Alerts</h3>
          <ul>
            {health.alerts.map((alert, index) => (
              <li key={index} className="alert-item">{alert}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AuditLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    actionType: '',
    resourceType: '',
    startDate: '',
    endDate: '',
  });

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const options: Record<string, string | number> = {};
      if (filters.actionType) options.actionType = filters.actionType;
      if (filters.resourceType) options.resourceType = filters.resourceType;
      if (filters.startDate) options.startDate = filters.startDate;
      if (filters.endDate) options.endDate = filters.endDate;
      options.limit = 100;

      const response = await systemSettingsService.listAuditLogs(options);
      setLogs(response.logs);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters.actionType, filters.resourceType, filters.startDate, filters.endDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
  };

  const columns: TableColumn<AuditLog>[] = [
    {
      header: 'Timestamp',
      accessor: 'timestamp',
      render: (value) => new Date(value as string).toLocaleString(),
    },
    {
      header: 'Admin',
      accessor: 'adminEmail',
    },
    {
      header: 'Action',
      accessor: 'actionType',
    },
    {
      header: 'Resource',
      accessor: 'resourceType',
      hideOnTablet: true,
    },
    {
      header: 'Resource ID',
      accessor: 'resourceId',
      render: (value) => (value as string) || '-',
      hideOnTablet: true,
    },
  ];

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}
      <div className="tab-header">
        <h2>Audit Logs</h2>
        <button onClick={loadLogs} className="refresh-button">Refresh</button>
      </div>

      <div className="audit-filters">
        <div className="filter-group">
          <label>Action Type</label>
          <input
            type="text"
            value={filters.actionType}
            onChange={(e) => handleFilterChange('actionType', e.target.value)}
            placeholder="Filter by action type"
          />
        </div>
        <div className="filter-group">
          <label>Resource Type</label>
          <input
            type="text"
            value={filters.resourceType}
            onChange={(e) => handleFilterChange('resourceType', e.target.value)}
            placeholder="Filter by resource type"
          />
        </div>
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
        <button onClick={loadLogs} className="apply-filters-button">Apply Filters</button>
      </div>

      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : (
        <Table
          columns={columns}
          data={logs}
          getRowId={(row) => row.logId}
          emptyMessage="No audit logs found"
        />
      )}
    </div>
  );
};

const DataToolsTab: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportedData, setExportedData] = useState<Record<string, unknown> | null>(null);

  const handleExport = async () => {
    if (!userId) {
      alert('Please enter a user ID');
      return;
    }
    try {
      setLoading(true);
      const data = await systemSettingsService.exportUserData(userId);
      setExportedData(data);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to export user data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) {
      alert('Please enter a user ID');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete all data for user ${userId}? This action cannot be undone!`)) {
      return;
    }
    try {
      setLoading(true);
      await systemSettingsService.deleteUserData(userId);
      alert('User data deleted successfully');
      setUserId('');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete user data');
    } finally {
      setLoading(false);
    }
  };

  const downloadExport = () => {
    if (!exportedData) return;
    const blob = new Blob([JSON.stringify(exportedData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-data-${userId}-${new Date().toISOString()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Data Export & Deletion Tools (GDPR Compliance)</h2>
      </div>

      <div className="data-tools-section">
        <div className="tool-card">
          <h3>Export User Data</h3>
          <p>Export all data associated with a user for GDPR compliance.</p>
          <div className="tool-form">
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter User ID"
              className="user-id-input"
            />
            <button onClick={handleExport} disabled={loading} className="export-data-button">
              {loading ? 'Exporting...' : 'Export Data'}
            </button>
          </div>
          {exportedData && (
            <div className="export-result">
              <h4>Exported Data</h4>
              <button onClick={downloadExport} className="download-button">Download JSON</button>
              <pre className="export-preview">{JSON.stringify(exportedData, null, 2)}</pre>
            </div>
          )}
        </div>

        <div className="tool-card danger">
          <h3>Delete User Data</h3>
          <p className="warning-text">⚠️ This will permanently delete all data associated with a user. This action cannot be undone!</p>
          <div className="tool-form">
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter User ID"
              className="user-id-input"
            />
            <button onClick={handleDelete} disabled={loading} className="delete-data-button">
              {loading ? 'Deleting...' : 'Delete User Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigModal: React.FC<{ config?: PlatformConfig; onClose: () => void }> = ({ config, onClose }) => {
  const [formData, setFormData] = useState({
    configKey: config?.configKey || '',
    configValue: config?.configValue ? (typeof config.configValue === 'object' ? JSON.stringify(config.configValue, null, 2) : String(config.configValue)) : '',
    description: config?.description || '',
    category: config?.category || 'general',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let parsedValue: string | number | boolean | Record<string, unknown> = formData.configValue;
      try {
        parsedValue = JSON.parse(formData.configValue);
      } catch {
        if (formData.configValue === 'true' || formData.configValue === 'false') {
          parsedValue = formData.configValue === 'true';
        } else if (!isNaN(Number(formData.configValue))) {
          parsedValue = Number(formData.configValue);
        }
      }

      if (config) {
        await systemSettingsService.updatePlatformConfig(config.configKey, {
          configValue: parsedValue,
          description: formData.description || undefined,
          category: formData.category,
        });
      } else {
        await systemSettingsService.createPlatformConfig({
          configKey: formData.configKey,
          configValue: parsedValue,
          description: formData.description || undefined,
          category: formData.category,
        });
      }
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{config ? 'Edit' : 'Create'} Platform Config</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Config Key {!config && '*'}</label>
            <input
              type="text"
              value={formData.configKey}
              onChange={(e) => setFormData({ ...formData, configKey: e.target.value })}
              required={!config}
              disabled={!!config}
            />
          </div>
          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category}
              onChange={(e) =>
              setFormData({
                ...formData,
                category: e.target.value as
                  | 'pricing'
                  | 'booking'
                  | 'general'
                  | 'commission'
                  | 'security'
                  | 'notification',
              })
            }
              required
            >
              <option value="pricing">Pricing</option>
              <option value="commission">Commission</option>
              <option value="booking">Booking</option>
              <option value="general">General</option>
              <option value="security">Security</option>
              <option value="notification">Notification</option>
            </select>
          </div>
          <div className="form-group">
            <label>Config Value *</label>
            <textarea
              value={formData.configValue}
              onChange={(e) => setFormData({ ...formData, configValue: e.target.value })}
              required
              rows={5}
              placeholder='Enter value (string, number, boolean, or JSON)'
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CategoryModal: React.FC<{ category?: ServiceCategory; onClose: () => void }> = ({ category, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    icon: category?.icon || '',
    isActive: category?.isActive ?? true,
    displayOrder: category?.displayOrder || 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (category) {
        await systemSettingsService.updateServiceCategory(category.categoryId, formData);
      } else {
        await systemSettingsService.createServiceCategory(formData);
      }
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{category ? 'Edit' : 'Create'} Service Category</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Icon</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="Icon name or URL"
            />
          </div>
          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value, 10) })}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NotificationTemplateModal: React.FC<{ template?: NotificationTemplate; onClose: () => void }> = ({ template, onClose }) => {
  const [formData, setFormData] = useState({
    templateType: template?.templateType || '',
    title: template?.title || '',
    body: template?.body || '',
    isActive: template?.isActive ?? true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (template) {
        await systemSettingsService.updateNotificationTemplate(template.templateId, formData);
      } else {
        await systemSettingsService.createNotificationTemplate(formData);
      }
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{template ? 'Edit' : 'Create'} Notification Template</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Template Type *</label>
            <input
              type="text"
              value={formData.templateType}
              onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Body *</label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              required
              rows={6}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EmailTemplateModal: React.FC<{ template?: EmailTemplate; onClose: () => void }> = ({ template, onClose }) => {
  const [formData, setFormData] = useState({
    templateType: template?.templateType || '',
    subject: template?.subject || '',
    htmlBody: template?.htmlBody || '',
    textBody: template?.textBody || '',
    isActive: template?.isActive ?? true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (template) {
        await systemSettingsService.updateEmailTemplate(template.templateId, formData);
      } else {
        await systemSettingsService.createEmailTemplate(formData);
      }
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{template ? 'Edit' : 'Create'} Email Template</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Template Type *</label>
            <input
              type="text"
              value={formData.templateType}
              onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Subject *</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>HTML Body *</label>
            <textarea
              value={formData.htmlBody}
              onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
              required
              rows={10}
            />
          </div>
          <div className="form-group">
            <label>Text Body</label>
            <textarea
              value={formData.textBody}
              onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
              rows={6}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
